import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import React from 'react';
import { DEFAULT_US_LAT_LNG } from '@/lib/constants';
import { EditableDealerInfo } from '../../../../lib/schema/dealer/types';
import GrayedOut from '../../../atoms/GrayedOut';
import Visibility from '../DealerBenefits/Visibility';
import { defaultHours, EditableHours, ReadonlyHours } from './Hours';

// Dynamically import DealerMap to avoid SSR issues with Leaflet
const DealerMap = dynamic(
  () =>
    import('@/components/molecules/LeafletMaps/DealerMap').then((mod) => ({
      default: mod.DealerMap,
    })),
  { ssr: false }
);

export interface LocationInfo {
  address: string;
  city: string;
  state: string;
  postal: string;
}

interface DealerLocationProps {
  dealerName: string;
  location: LocationInfo;
  phone: string;
  editableDealerInfo: EditableDealerInfo;
  draftEditableDealerInfo: EditableDealerInfo;
  setDraftEditableDealerInfo: (value: EditableDealerInfo) => void;
  website?: string;
  editing: boolean;
}

const DealerLocation: React.FC<DealerLocationProps> = ({
  dealerName,
  location,
  phone,
  editableDealerInfo,
  draftEditableDealerInfo,
  setDraftEditableDealerInfo,
  website,
  editing,
}) => {
  const fetchGeocodeLocationByPostalCode = async () => {
    try {
      const resp = await fetch(
        `/api/geo/geocode?${new URLSearchParams({ postalCode: location.postal }).toString()}`
      );
      if (resp.ok) {
        const geocodeResponseData: { coordinates: [number, number] } =
          await resp.json();
        const [lat, lng] = geocodeResponseData.coordinates;
        return { lat, lng };
      }
      return DEFAULT_US_LAT_LNG;
    } catch {
      return DEFAULT_US_LAT_LNG;
    }
  };

  const { data: center } = useQuery({
    queryKey: ['geocodeLocationByPostalCode'],
    queryFn: fetchGeocodeLocationByPostalCode,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="pb-3xl pt-xl m:pb-[80px]">
      <div className="mx-auto max-w-[1096px] overflow-hidden rounded-small m:flex">
        <div
          className={clsx('flex flex-col gap-l bg-white p-xl', {
            'm:max-w-[min(50%,400px)]': !editing,
          })}
        >
          {/* name */}
          <GrayedOut gray={editing}>
            <div className="text-h3SemiBold text-neutral-900">{dealerName}</div>
          </GrayedOut>

          <div className="flex flex-col gap-l text-body2Light text-neutral-800">
            {/* address */}
            <GrayedOut gray={editing}>
              <div>
                <p>{location.address}</p>
                <p>
                  {location.city}, {location.state} {location.postal}
                </p>
              </div>
            </GrayedOut>
            {/* phone */}
            <GrayedOut gray={editing}>
              <div>
                Call:{' '}
                <a
                  className="text-body2Regular text-blue-medium"
                  href={`tel:${phone}`}
                >
                  {phone}
                </a>
              </div>
            </GrayedOut>
            {/* hours */}
            {!editing &&
              editableDealerInfo.hoursOfOperation &&
              editableDealerInfo.showHours && (
                <ReadonlyHours hours={editableDealerInfo.hoursOfOperation} />
              )}{' '}
            {editing && (
              <div className="flex flex-col gap-s">
                <Visibility
                  visible={draftEditableDealerInfo.showHours}
                  setVisible={(vis) =>
                    setDraftEditableDealerInfo({
                      ...draftEditableDealerInfo,
                      showHours: vis,
                    })
                  }
                />
                <EditableHours
                  hours={
                    draftEditableDealerInfo.hoursOfOperation ?? defaultHours
                  }
                  setHours={(hours) =>
                    setDraftEditableDealerInfo({
                      ...draftEditableDealerInfo,
                      hoursOfOperation: hours,
                    })
                  }
                />
              </div>
            )}
            {/* site */}
            {website ? (
              <GrayedOut gray={editing}>
                <a
                  className="text-body1Regular text-blue-medium"
                  href={website}
                >
                  Visit website
                </a>
              </GrayedOut>
            ) : null}
          </div>
        </div>
        <GrayedOut gray={editing} className="grow">
          <div className="h-[300px] min-h-[300px] w-full m:h-full">
            <DealerMap
              defaultCenter={{ lat: 0, lng: 0 }}
              center={center}
              defaultZoom={13}
              infoWindowContent={
                <div>
                  <h1 className="mb-s text-h3SemiBold">
                    {location.city}, {location.state}
                  </h1>
                  <div className="mb-s">
                    <div>{location.address}</div>
                    <div>
                      {location.city}, {location.state} {location.postal}
                    </div>
                  </div>
                  <div>
                    Contact:{' '}
                    <a href={`tel:${phone}`} className="text-blue-medium">
                      {phone}
                    </a>
                  </div>
                </div>
              }
            />
          </div>
        </GrayedOut>
      </div>
    </div>
  );
};

export default DealerLocation;
